import { parseColor } from '@ark-ui/react'
import { usePrevious } from '@mantine/hooks'
import { LinePaintBrush } from '@qr-front/icons/line-paint-brush'
import { theme } from '@qr-front/ui'
import { SmartScrollContainer } from '@qr-front/ui/components/SmartScrollContainer'
import { cn, getVersionForClone, isColorWhite } from '@qr-front/ui/utils'
import * as RadioGroup from '@radix-ui/react-radio-group'
import { atom, useAtom } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import {
	forwardRef,
	ForwardRefExoticComponent,
	RefAttributes,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react'

import { PrimaryAndSecondaryColorFields } from '@/components/PrimaryAndSecondaryColorFields'
import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'

import { captureBuilderEvent } from '../../builderAnalytics'
import { qrColorSchemeList, TQrColorScheme } from '../qrColorSchemes'
import {
	TContentFormHandle,
	TContentFormSectionPartial,
	TContentFormSectionPartialProps,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { useSyncFormSectionValidity } from '../useSyncFormSectionValidity'

export const CONTENT_DESIGN_FORM_ID = 'content-design'

export const colorSchemeOptionListAtom = atom(
	JSON.parse(JSON.stringify(qrColorSchemeList)) as TQrColorScheme[],
)

export const selectedColorSchemeAtom = atomWithReset<string>(
	qrColorSchemeList[0].name,
)

export const primaryColorAtom = atomWithReset<string>(
	theme.colors['qr-color-scheme-1'].primary,
)

export const secondaryColorAtom = atomWithReset<string>(
	theme.colors['qr-color-scheme-1'].secondary,
)

export const resetContentDesignForm = () => {
	getStore().set(
		colorSchemeOptionListAtom,
		JSON.parse(JSON.stringify(qrColorSchemeList)),
	)
	getStore().set(selectedColorSchemeAtom, RESET)
	getStore().set(primaryColorAtom, RESET)
	getStore().set(secondaryColorAtom, RESET)
}

const ColorSchemePreview = ({
	colorScheme: { name, primary, secondary },
}: {
	colorScheme: TQrColorScheme
}) => {
	const swatchCls = (color: string) =>
		cn(
			'h-8 rounded-6 border border-transparent transition-colors',
			isColorWhite(parseColor)(color) && 'border-border-grey',
			getVersionForClone({
				base: 'w-10 rounded-6',
				clone1: 'w-[42px] rounded-full',
			}),
		)

	return (
		<RadioGroup.Item
			value={name}
			className={cn(
				'relative flex p-2 ring-1 ring-inset ring-border-grey transition data-[state=checked]:ring-2 data-[state=checked]:ring-blue hover:ring-2',
				getVersionForClone({
					base: 'gap-2 rounded-10',
					clone1: 'gap-1 rounded-full',
				}),
			)}
		>
			<div
				className={swatchCls(primary)}
				style={{
					backgroundColor: primary,
				}}
			/>
			<div
				className={swatchCls(secondary)}
				style={{
					backgroundColor: secondary,
				}}
			/>
		</RadioGroup.Item>
	)
}

const ContentDesignForm = forwardRef<
	TContentFormHandle,
	{
		AdditionalFields?: ForwardRefExoticComponent<
			TContentFormSectionPartialProps & RefAttributes<TContentFormHandle>
		>
	}
>(({ AdditionalFields }, ref) => {
	const t = useTranslations()

	const [isAdditionalFieldsValid, setIsAdditionalFieldsValid] =
		useState(false)

	useSyncFormSectionValidity({
		formId: CONTENT_DESIGN_FORM_ID,
		isValid: AdditionalFields ? isAdditionalFieldsValid : true,
	})

	const additionalFieldsRef = useRef<TContentFormHandle>(null)

	useImperativeHandle(
		ref,
		() => ({
			validate: () => {
				if (AdditionalFields) {
					return additionalFieldsRef!.current!.validate()
				}
				return Promise.resolve({ isValid: true })
			},
		}),
		[AdditionalFields],
	)

	const [optionList, setOptionList] = useAtom(colorSchemeOptionListAtom)
	const [selectedOptionName, setSelectedOptionName] = useAtom(
		selectedColorSchemeAtom,
	)
	const prevSelectedOptionName = usePrevious(selectedOptionName)
	const [primaryColor, setPrimaryColor] = useAtom(primaryColorAtom)
	const [secondaryColor, setSecondaryColor] = useAtom(secondaryColorAtom)

	useEffect(() => {
		if (selectedOptionName === prevSelectedOptionName) {
			setOptionList((prev) =>
				prev.map((option) =>
					option.name === selectedOptionName
						? {
								...option,
								primary: primaryColor,
								secondary: secondaryColor,
							}
						: option,
				),
			)
		}
	}, [
		prevSelectedOptionName,
		primaryColor,
		secondaryColor,
		selectedOptionName,
		setOptionList,
	])

	const handleOptionChange = (optionName: string) => {
		setSelectedOptionName(optionName)
		const selectedOptionIndex = optionList.findIndex(
			({ name }) => name === optionName,
		)
		const selectedOption = optionList[selectedOptionIndex]
		if (selectedOption) {
			setPrimaryColor(selectedOption.primary)
			setSecondaryColor(selectedOption.secondary)
			captureBuilderEvent('builder_select_color', {
				design_scope: 'landing_page',
				color_element: 'preset',
				color_value: selectedOption.primary,
				preset_position: selectedOptionIndex + 1,
				input_method: 'preset',
			})
		}
	}

	return (
		<fieldset>
			<SmartScrollContainer>
				<RadioGroup.Root
					value={selectedOptionName}
					onValueChange={handleOptionChange}
					className="flex max-w-full justify-between gap-4"
				>
					{optionList.map((colorScheme) => (
						<ColorSchemePreview
							key={colorScheme.name}
							colorScheme={colorScheme}
						/>
					))}
				</RadioGroup.Root>
			</SmartScrollContainer>
			<div className="mt-6 rounded-10 bg-light-grey-70 p-4 desktop:mt-8 desktop:p-6">
				<PrimaryAndSecondaryColorFields
					primaryColor={primaryColor}
					setPrimaryColor={setPrimaryColor}
					primaryLabel={t(
						'generator__content_form_section__design__primary_color',
					)}
					secondaryColor={secondaryColor}
					setSecondaryColor={setSecondaryColor}
					secondaryLabel={t(
						'generator__content_form_section__design__secondary_color',
					)}
					onColorChange={({
						colorElement,
						colorValue,
						inputMethod,
					}) =>
						captureBuilderEvent('builder_select_color', {
							design_scope: 'landing_page',
							color_element: colorElement,
							color_value: colorValue,
							input_method: inputMethod,
						})
					}
				/>
			</div>
			{AdditionalFields ? (
				<AdditionalFields
					ref={additionalFieldsRef}
					onValidChange={setIsAdditionalFieldsValid}
				/>
			) : null}
		</fieldset>
	)
})

ContentDesignForm.displayName = 'ContentDesignForm'

export const contentDesignFormConfig = (
	t: TTranslationFunction,
	AdditionalFields?: TContentFormSectionPartial,
	disabled?: boolean,
): TQrContentFormSection => {
	const Content = forwardRef<TContentFormHandle>((_, ref) => (
		<ContentDesignForm ref={ref} AdditionalFields={AdditionalFields} />
	))

	Content.displayName = 'Content'

	return {
		id: CONTENT_DESIGN_FORM_ID,
		title: t('generator__content_form_section__design__title'),
		description: t('generator__content_form_section__design__description'),
		icon: <LinePaintBrush />,
		Content,
		disabled,
	}
}
